/* Smooth scroll (§13, optional). Eases mouse-wheel and trackpad scrolling by driving the window's
   native scroll position, so position: sticky and the scroll engine keep working. Easing is
   frame-rate independent (same glide at 60 and 144 Hz); "Smooth scroll softness" sets its length.
   Same-page anchor links glide too. Touch, keyboard and scrollbar scrolling stay native, and any
   key or pointer press mid-glide hands control straight back. Off for touch / coarse pointers and
   reduced motion; pauses while a drawer locks scrolling (theme:scroll-lock) or a header menu is
   open, and inside scrollable panels. */
(() => {
  const fine = matchMedia('(pointer: fine)');
  const theme = window.Theme;
  if (!theme || theme.smoothScroll || !fine.matches || !theme.motionAllowed()) return;

  // Softness 1–10 → damping rate per second. 8 (default) settles in ~0.8 s, 10 in ~1.2 s, 1 in ~0.35 s.
  const softness = Math.min(10, Math.max(1, Number(theme.config.smoothSoftness) || 8));
  const lambda = 14 - softness;
  const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);

  let target = window.scrollY;
  let current = window.scrollY;
  let frame = 0;
  let last = 0;
  let paused = false;

  const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;
  const clamp = (y) => Math.max(0, Math.min(maxScroll(), y));

  // Let nested scroll areas (drawers, filter panels, chip rows) keep the wheel when they can still scroll.
  function scrollableParent(el, deltaY) {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (!/(auto|scroll)/.test(style.overflowY) || node.scrollHeight <= node.clientHeight) continue;
      const atTop = node.scrollTop <= 0;
      const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
      if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return node;
    }
    return null;
  }

  function tick(now) {
    const dt = Math.min(0.05, (now - last) / 1000 || 1 / 60);
    last = now;
    current += (target - current) * (1 - Math.exp(-lambda * dt));
    if (Math.abs(target - current) < 0.4) current = target;
    window.scrollTo(0, current);
    frame = current === target ? 0 : requestAnimationFrame(tick);
  }

  function start() {
    if (frame) return;
    last = performance.now();
    frame = requestAnimationFrame(tick);
  }

  function stop() {
    cancelAnimationFrame(frame);
    frame = 0;
    target = current = window.scrollY;
  }

  function scrollTo(y) {
    if (paused) return window.scrollTo(0, y);
    if (!frame) current = window.scrollY;
    target = clamp(y);
    start();
  }

  window.addEventListener(
    'wheel',
    (event) => {
      if (paused || event.ctrlKey || event.defaultPrevented || !theme.motionAllowed()) return;
      if (document.querySelector('.hdr__item.is-open') || scrollableParent(event.target, event.deltaY)) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      event.preventDefault();
      if (!frame) target = current = window.scrollY;
      target = clamp(target + event.deltaY * unit);
      start();
    },
    { passive: false }
  );

  // Same-page anchors ("Back to top", in-page links) glide instead of jumping. The skip link stays instant.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (link.classList.contains('skip-link') || link.hasAttribute('data-dialog-open')) return;
    const url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
    const id = decodeURIComponent(url.hash.slice(1));
    const el = id ? document.getElementById(id) || (id === 'top' ? document.body : null) : document.body;
    if (!el) return;
    event.preventDefault();
    const margin = el === document.body ? 0 : parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    scrollTo(el === document.body ? 0 : el.getBoundingClientRect().top + window.scrollY - margin);
    if (url.hash !== location.hash) history.pushState(null, '', url.hash);
    if (el === document.body) return;
    // Move focus with the scroll, as a native anchor jump would.
    if (!el.matches('a[href], button, input, select, textarea, [tabindex]')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  });

  // Keyboard, scrollbar or other scripts take over: follow the native position instead of fighting it.
  window.addEventListener('keydown', (event) => { if (frame && SCROLL_KEYS.has(event.key)) stop(); });
  window.addEventListener('pointerdown', () => { if (frame) stop(); }, { passive: true });
  window.addEventListener('scroll', () => {
    if (!frame) target = current = window.scrollY;
  }, { passive: true });
  window.addEventListener('resize', () => {
    target = clamp(target);
  }, { passive: true });

  document.addEventListener('theme:scroll-lock', (event) => {
    paused = event.detail.locked;
    stop();
  });

  theme.smoothScroll = { stop, scrollTo };
})();
